import React, { useEffect, useState } from 'react';
import {
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  CircularProgress, 
  Box, 
  Button, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  MenuItem, 
  Select, 
  InputLabel, 
  FormControl, 
  Snackbar, 
  Tooltip,
  Card,
  CardContent,
  Chip,
  Avatar,
  Grid,
  Divider,
  InputAdornment,
} from '@mui/material';
import { 
  Add, 
  Edit, 
  Delete, 
  Brush,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Store as StoreIcon,
  Clear as ClearIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import apiService from '../services/api';
import AccessionService from '../services/accession-service';

interface Location {
  id: string;
  labCode: string;
  labName: string;
  locationType: string;
  departmentType: string;
  city: string;
  state: string;
  contactName: string;
  institutionId: string | null;
  lab_sites?: Location[];
  logo?: string;
  themeColor?: string;
  splashBanner?: string;
  // Accession number configuration
  accessionPart1Type?: string;
  accessionPart1Custom?: string;
  accessionPart2Type?: string;
  accessionPart2Custom?: string;
  accessionPart3Type?: string;
  accessionPart3Custom?: string;
}

const defaultForm = {
  labCode: '',
  labName: '',
  locationType: 'HQ',
  departmentType: '',
  institutionId: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  phoneNumber: '',
  contactName: '',
  isActive: true,
  defaultForSpecimenClass: '',
  // Accession number configuration
  accessionPart1Type: 'custom',
  accessionPart1Custom: '',
  accessionPart2Type: 'year',
  accessionPart2Custom: '',
  accessionPart3Type: 'patientId',
  accessionPart3Custom: '',
};

const Locations: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<Location | null>(null);
  const [form, setForm] = useState<any>(defaultForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success'|'error'}>({open: false, message: '', severity: 'success'});
  const [brandingDialog, setBrandingDialog] = useState<{open: boolean, location: Location | null}>({open: false, location: null});
  const [branding, setBranding] = useState<{logo: string|null, theme_color: string, splash_banner: string|null}>({logo: null, theme_color: '', splash_banner: null});
  const [brandingPreview, setBrandingPreview] = useState<{logo: string|null, splash_banner: string|null}>({logo: null, splash_banner: null});
  const [accessionDialog, setAccessionDialog] = useState<{open: boolean, location: Location | null}>({open: false, location: null});
  const [accessionConfig, setAccessionConfig] = useState<{
    part1Type: string;
    part1Custom: string;
    part2Type: string;
    part2Custom: string;
    part3Type: string;
    part3Custom: string;
  }>({
    part1Type: 'custom',
    part1Custom: '',
    part2Type: 'year',
    part2Custom: '',
    part3Type: 'patientId',
    part3Custom: '',
  });
  const colorPalette = ['#1976d2','#388e3c','#d32f2f','#fbc02d','#7b1fa2','#00838f','#c2185b','#ffa000','#455a64','#5d4037'];

  // Fetch locations hierarchy
  const fetchLocations = async () => {
    setLoading(true);
    try {
      const data = await apiService.getLocationsHierarchy();
      setLocations(data);
    } catch (e) {
      setLocations([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchLocations(); }, []);

  // Flatten all locations for search and HQ selection
  const flattenLocations = (locs: Location[]): Location[] => {
    let all: Location[] = [];
    for (const hq of locs) {
      all.push(hq);
      if (hq.lab_sites) all = all.concat(hq.lab_sites);
    }
    return all;
  };
  const allLocations = flattenLocations(locations);
  const hqOptions = allLocations.filter(l => l.locationType === 'HQ');

  // Filtered locations for search
  const filterFn = (loc: Location) => {
    const q = search.toLowerCase();
    return (
      loc.labCode.toLowerCase().includes(q) ||
      loc.labName.toLowerCase().includes(q) ||
      loc.city.toLowerCase().includes(q)
    );
  };
  const filteredLocations = search
    ? locations
        .map(hq => ({
          ...hq,
          lab_sites: hq.lab_sites?.filter(filterFn) || [],
        }))
        .filter(hq => filterFn(hq) || (hq.lab_sites && hq.lab_sites.length > 0))
    : locations;

  const handleOpenDialog = (loc?: Location) => {
    if (loc) {
      setEditing(loc);
      setForm({ ...loc });
    } else {
      setEditing(null);
      setForm(defaultForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditing(null);
    setForm(defaultForm);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setForm((prev: any) => ({ ...prev, [name!]: value }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setForm((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await apiService.updateLocation(editing.id, form);
      } else {
        await apiService.createLocation(form);
      }
      setSnackbar({ open: true, message: `Location ${editing ? 'updated' : 'created'} successfully`, severity: 'success' });
      handleCloseDialog();
      fetchLocations();
    } catch (error) {
      setSnackbar({ open: true, message: `Failed to ${editing ? 'update' : 'create'} location`, severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await apiService.deleteLocation(deleteId);
      setSnackbar({ open: true, message: 'Location deleted successfully', severity: 'success' });
      setDeleteId(null);
      fetchLocations();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete location', severity: 'error' });
    }
  };

  function getBase64Preview(val: any): string | null {
    if (!val) return null;
    if (typeof val === 'string') {
      if (val.startsWith('data:')) return val;
      if (val.startsWith('http')) return val;
    }
    if (val instanceof File) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(val);
      }) as any;
    }
    return null;
  }

  const handleOpenBranding = (loc: Location) => {
    setBrandingDialog({ open: true, location: loc });
    setBranding({
      logo: loc.logo || null,
      theme_color: loc.themeColor || '',
      splash_banner: loc.splashBanner || null
    });
    setBrandingPreview({
      logo: loc.logo || null,
      splash_banner: loc.splashBanner || null
    });
  };

  const handleCloseBranding = () => {
    setBrandingDialog({ open: false, location: null });
    setBranding({ logo: null, theme_color: '', splash_banner: null });
    setBrandingPreview({ logo: null, splash_banner: null });
  };

  const handleBrandingFile = (field: 'logo'|'splash_banner', file: File|null) => {
    if (file) {
      const preview = getBase64Preview(file);
      setBranding(prev => ({ ...prev, [field]: file }));
      setBrandingPreview(prev => ({ ...prev, [field]: preview }));
    } else {
      setBranding(prev => ({ ...prev, [field]: null }));
      setBrandingPreview(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleBrandingColor = (color: string) => {
    setBranding(prev => ({ ...prev, theme_color: color }));
  };

  const handleSaveBranding = async () => {
    if (!brandingDialog.location) return;
    try {
      await apiService.updateLocationBranding(brandingDialog.location.id, branding);
      setSnackbar({ open: true, message: 'Branding updated successfully', severity: 'success' });
      handleCloseBranding();
      fetchLocations();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update branding', severity: 'error' });
    }
  };

  const handleRemoveBrandingImage = (field: 'logo'|'splash_banner') => {
    setBranding(prev => ({ ...prev, [field]: null }));
    setBrandingPreview(prev => ({ ...prev, [field]: null }));
  };

  // Accession configuration handlers
  const handleOpenAccession = (loc: Location) => {
    setAccessionDialog({ open: true, location: loc });
    setAccessionConfig({
      part1Type: loc.accessionPart1Type || 'custom',
      part1Custom: loc.accessionPart1Custom || '',
      part2Type: loc.accessionPart2Type || 'year',
      part2Custom: loc.accessionPart2Custom || '',
      part3Type: loc.accessionPart3Type || 'patientId',
      part3Custom: loc.accessionPart3Custom || '',
    });
  };

  const handleCloseAccession = () => {
    setAccessionDialog({ open: false, location: null });
    setAccessionConfig({
      part1Type: 'custom',
      part1Custom: '',
      part2Type: 'year',
      part2Custom: '',
      part3Type: 'patientId',
      part3Custom: '',
    });
  };

  const handleAccessionConfigChange = (part: string, field: string, value: string) => {
    setAccessionConfig(prev => ({
      ...prev,
      [`part${part}${field}`]: value
    }));
  };

  const handleSaveAccession = async () => {
    if (!accessionDialog.location) return;
    
    // Validate configuration
    const validation = AccessionService.validateConfig({
      part1Type: accessionConfig.part1Type,
      part1Custom: accessionConfig.part1Custom,
      part2Type: accessionConfig.part2Type,
      part2Custom: accessionConfig.part2Custom,
      part3Type: accessionConfig.part3Type,
      part3Custom: accessionConfig.part3Custom,
    });
    
    if (!validation.isValid) {
      setSnackbar({ 
        open: true, 
        message: `Validation failed: ${validation.errors.join(', ')}`, 
        severity: 'error' 
      });
      return;
    }
    
    try {
      const updateData = {
        accessionPart1Type: accessionConfig.part1Type,
        accessionPart1Custom: accessionConfig.part1Custom,
        accessionPart2Type: accessionConfig.part2Type,
        accessionPart2Custom: accessionConfig.part2Custom,
        accessionPart3Type: accessionConfig.part3Type,
        accessionPart3Custom: accessionConfig.part3Custom,
      };
      await apiService.updateLocation(accessionDialog.location.id, updateData);
      setSnackbar({ open: true, message: 'Accession configuration updated successfully', severity: 'success' });
      handleCloseAccession();
      fetchLocations();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update accession configuration', severity: 'error' });
    }
  };

  const generateAccessionPreview = () => {
    return AccessionService.generatePreview({
      part1Type: accessionConfig.part1Type,
      part1Custom: accessionConfig.part1Custom,
      part2Type: accessionConfig.part2Type,
      part2Custom: accessionConfig.part2Custom,
      part3Type: accessionConfig.part3Type,
      part3Custom: accessionConfig.part3Custom,
    });
  };

  const getLocationTypeIcon = (type: string) => {
    switch (type) {
      case 'HQ':
        return <HomeIcon sx={{ fontSize: 16, color: '#1976d2' }} />;
      case 'Lab Site':
        return <StoreIcon sx={{ fontSize: 16, color: '#388e3c' }} />;
      default:
        return <BusinessIcon sx={{ fontSize: 16, color: '#666' }} />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a237e' }}>
            Locations
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage laboratory locations and branding
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
            }
          }}
        >
          Add Location
        </Button>
      </Box>

      {/* Search Box */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by lab code, name, or city..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#666' }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearch('')}
                    edge="end"
                    sx={{ color: '#666' }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Locations Grid */}
      <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <Box display="flex" flexDirection="column" alignItems="center">
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                Loading locations...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we fetch the data
              </Typography>
            </Box>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <BusinessIcon sx={{ fontSize: 18 }} />
                      Lab Code
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationIcon sx={{ fontSize: 18 }} />
                      Lab Name
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <HomeIcon sx={{ fontSize: 18 }} />
                      Type
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                    Department
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                    City
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                    State
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon sx={{ fontSize: 18 }} />
                      Contact
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLocations.map(hq => (
                  <React.Fragment key={hq.id}>
                    <TableRow sx={{ 
                      backgroundColor: '#f0f8ff',
                      '&:hover': { 
                        backgroundColor: '#e3f2fd',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar 
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              bgcolor: '#1976d2',
                              fontSize: '0.875rem'
                            }}
                          >
                            {hq.labCode?.charAt(0)?.toUpperCase() || 'L'}
                          </Avatar>
                          <Chip 
                            label={hq.labCode} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {hq.labName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getLocationTypeIcon(hq.locationType)}
                          <Chip 
                            label={hq.locationType}
                            size="small"
                            color={hq.locationType === 'HQ' ? 'primary' : 'secondary'}
                            sx={{ fontWeight: 500 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {hq.departmentType || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {hq.city}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {hq.state}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon sx={{ fontSize: 14, color: '#666' }} />
                          <Typography variant="body2" fontSize="0.75rem">
                            {hq.contactName || 'No contact'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Edit">
                            <IconButton 
                              onClick={() => handleOpenDialog(hq)}
                              size="small"
                              sx={{ 
                                color: '#1976d2',
                                '&:hover': { 
                                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              onClick={() => setDeleteId(hq.id)}
                              size="small"
                              sx={{ 
                                color: '#d32f2f',
                                '&:hover': { 
                                  backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Branding">
                            <IconButton 
                              onClick={() => handleOpenBranding(hq)}
                              size="small"
                              sx={{ 
                                color: '#9c27b0',
                                '&:hover': { 
                                  backgroundColor: 'rgba(156, 39, 176, 0.1)',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <Brush fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Accession Config">
                            <IconButton 
                              onClick={() => handleOpenAccession(hq)}
                              size="small"
                              sx={{ 
                                color: '#ff9800',
                                '&:hover': { 
                                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <SettingsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {hq.lab_sites && hq.lab_sites.filter(filterFn).map(site => (
                      <TableRow 
                        key={site.id}
                        sx={{ 
                          '&:hover': { 
                            backgroundColor: '#f8f9fa',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease-in-out'
                          },
                          '&:nth-of-type(even)': {
                            backgroundColor: '#fafafa'
                          }
                        }}
                      >
                        <TableCell sx={{ pl: 4 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar 
                              sx={{ 
                                width: 24, 
                                height: 24, 
                                bgcolor: '#388e3c',
                                fontSize: '0.75rem'
                              }}
                            >
                              {site.labCode?.charAt(0)?.toUpperCase() || 'S'}
                            </Avatar>
                            <Typography variant="body2" fontSize="0.75rem">
                              {site.labCode}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize="0.875rem">
                            {site.labName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getLocationTypeIcon(site.locationType)}
                            <Typography variant="body2" fontSize="0.75rem" sx={{ textTransform: 'capitalize' }}>
                              {site.locationType}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize="0.75rem" color="text.secondary">
                            {site.departmentType || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize="0.875rem">
                            {site.city}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize="0.875rem">
                            {site.state}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon sx={{ fontSize: 12, color: '#666' }} />
                            <Typography variant="body2" fontSize="0.7rem">
                              {site.contactName || 'No contact'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="Edit">
                              <IconButton 
                                onClick={() => handleOpenDialog(site)}
                                size="small"
                                sx={{ 
                                  color: '#1976d2',
                                  '&:hover': { 
                                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton 
                                onClick={() => setDeleteId(site.id)}
                                size="small"
                                sx={{ 
                                  color: '#d32f2f',
                                  '&:hover': { 
                                    backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Branding">
                              <IconButton 
                                onClick={() => handleOpenBranding(site)}
                                size="small"
                                sx={{ 
                                  color: '#9c27b0',
                                  '&:hover': { 
                                    backgroundColor: 'rgba(156, 39, 176, 0.1)',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <Brush fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Accession Config">
                              <IconButton 
                                onClick={() => handleOpenAccession(site)}
                                size="small"
                                sx={{ 
                                  color: '#ff9800',
                                  '&:hover': { 
                                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <SettingsIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
          color: 'white',
          fontWeight: 600
        }}>
          {editing ? 'Edit Location' : 'Add Location'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Lab Code" 
                name="labCode" 
                value={form.labCode} 
                onChange={handleFormChange} 
                fullWidth 
                sx={{ mb: 2 }} 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Lab Name" 
                name="labName" 
                value={form.labName} 
                onChange={handleFormChange} 
                fullWidth 
                sx={{ mb: 2 }} 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Type</InputLabel>
                <Select name="locationType" value={form.locationType} label="Type" onChange={handleSelectChange}>
                  <MenuItem value="HQ">HQ</MenuItem>
                  <MenuItem value="Lab Site">Lab Site</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {form.locationType === 'Lab Site' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Parent HQ</InputLabel>
                  <Select name="institutionId" value={form.institutionId} label="Parent HQ" onChange={handleSelectChange}>
                    {hqOptions.map(hq => (
                      <MenuItem key={hq.id} value={hq.id}>{hq.labName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Department" 
                name="departmentType" 
                value={form.departmentType} 
                onChange={handleFormChange} 
                fullWidth 
                sx={{ mb: 2 }} 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Address Line 1" 
                name="addressLine1" 
                value={form.addressLine1} 
                onChange={handleFormChange} 
                fullWidth 
                sx={{ mb: 2 }} 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Address Line 2" 
                name="addressLine2" 
                value={form.addressLine2} 
                onChange={handleFormChange} 
                fullWidth 
                sx={{ mb: 2 }} 
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                label="City" 
                name="city" 
                value={form.city} 
                onChange={handleFormChange} 
                fullWidth 
                sx={{ mb: 2 }} 
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                label="State" 
                name="state" 
                value={form.state} 
                onChange={handleFormChange} 
                fullWidth 
                sx={{ mb: 2 }} 
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                label="Zip Code" 
                name="zipCode" 
                value={form.zipCode} 
                onChange={handleFormChange} 
                fullWidth 
                sx={{ mb: 2 }} 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Country" 
                name="country" 
                value={form.country} 
                onChange={handleFormChange} 
                fullWidth 
                sx={{ mb: 2 }} 
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                label="Phone Number" 
                name="phoneNumber" 
                value={form.phoneNumber} 
                onChange={handleFormChange} 
                fullWidth 
                sx={{ mb: 2 }} 
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                label="Contact Name" 
                name="contactName" 
                value={form.contactName} 
                onChange={handleFormChange} 
                fullWidth 
                sx={{ mb: 2 }} 
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
              }
            }}
          >
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this location?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Branding Dialog */}
      <Dialog open={brandingDialog.open} onClose={handleCloseBranding} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)',
          color: 'white',
          fontWeight: 600
        }}>
          Branding - {brandingDialog.location?.labName}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Theme Color</Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {colorPalette.map(color => (
                  <Box
                    key={color}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: color,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      border: branding.theme_color === color ? '3px solid #333' : '2px solid #ddd',
                      '&:hover': { transform: 'scale(1.1)' }
                    }}
                    onClick={() => handleBrandingColor(color)}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Logo</Typography>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleBrandingFile('logo', e.target.files?.[0] || null)}
                style={{ marginBottom: 10 }}
              />
              {brandingPreview.logo && (
                <Box>
                  <img src={brandingPreview.logo} alt="Logo preview" style={{ maxWidth: 200, maxHeight: 100 }} />
                  <Button onClick={() => handleRemoveBrandingImage('logo')} size="small">Remove</Button>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Splash Banner</Typography>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleBrandingFile('splash_banner', e.target.files?.[0] || null)}
                style={{ marginBottom: 10 }}
              />
              {brandingPreview.splash_banner && (
                <Box>
                  <img src={brandingPreview.splash_banner} alt="Splash banner preview" style={{ maxWidth: 400, maxHeight: 200 }} />
                  <Button onClick={() => handleRemoveBrandingImage('splash_banner')} size="small">Remove</Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseBranding}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveBranding} 
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #7b1fa2 30%, #9c27b0 90%)',
              }
            }}
          >
            Save Branding
          </Button>
        </DialogActions>
      </Dialog>

      {/* Accession Configuration Dialog */}
      <Dialog open={accessionDialog.open} onClose={handleCloseAccession} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
          color: 'white',
          fontWeight: 600
        }}>
          Accession Number Configuration - {accessionDialog.location?.labName}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure the syntax for accession numbers. Each part can be custom text, year, patient ID, or location code. 
            A 5-character random suffix will be automatically added to all accession numbers.
          </Typography>
          
          <Grid container spacing={3}>
            {/* Part 1 */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Part 1</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select 
                      value={accessionConfig.part1Type} 
                      label="Type"
                      onChange={(e) => handleAccessionConfigChange('1', 'Type', e.target.value)}
                    >
                      <MenuItem value="custom">Custom</MenuItem>
                      <MenuItem value="year">Year</MenuItem>
                      <MenuItem value="patientId">Patient ID</MenuItem>
                      <MenuItem value="locationCode">Location Code</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  {accessionConfig.part1Type === 'custom' && (
                    <TextField
                      label="Custom Text (1-5 chars)"
                      value={accessionConfig.part1Custom}
                      onChange={(e) => handleAccessionConfigChange('1', 'Custom', e.target.value)}
                      fullWidth
                      inputProps={{ maxLength: 5 }}
                      helperText={`${accessionConfig.part1Custom.length}/5 characters`}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>

            {/* Part 2 */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Part 2</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select 
                      value={accessionConfig.part2Type} 
                      label="Type"
                      onChange={(e) => handleAccessionConfigChange('2', 'Type', e.target.value)}
                    >
                      <MenuItem value="custom">Custom</MenuItem>
                      <MenuItem value="year">Year</MenuItem>
                      <MenuItem value="patientId">Patient ID</MenuItem>
                      <MenuItem value="locationCode">Location Code</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  {accessionConfig.part2Type === 'custom' && (
                    <TextField
                      label="Custom Text (1-5 chars)"
                      value={accessionConfig.part2Custom}
                      onChange={(e) => handleAccessionConfigChange('2', 'Custom', e.target.value)}
                      fullWidth
                      inputProps={{ maxLength: 5 }}
                      helperText={`${accessionConfig.part2Custom.length}/5 characters`}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>

            {/* Part 3 */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Part 3</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select 
                      value={accessionConfig.part3Type} 
                      label="Type"
                      onChange={(e) => handleAccessionConfigChange('3', 'Type', e.target.value)}
                    >
                      <MenuItem value="custom">Custom</MenuItem>
                      <MenuItem value="year">Year</MenuItem>
                      <MenuItem value="patientId">Patient ID</MenuItem>
                      <MenuItem value="locationCode">Location Code</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  {accessionConfig.part3Type === 'custom' && (
                    <TextField
                      label="Custom Text (1-5 chars)"
                      value={accessionConfig.part3Custom}
                      onChange={(e) => handleAccessionConfigChange('3', 'Custom', e.target.value)}
                      fullWidth
                      inputProps={{ maxLength: 5 }}
                      helperText={`${accessionConfig.part3Custom.length}/5 characters`}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>

            {/* Preview */}
            <Grid item xs={12}>
              <Card sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom>Preview</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '1.2rem' }}>
                  {generateAccessionPreview()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Example accession number with current configuration
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseAccession}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveAccession} 
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
              }
            }}
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({...snackbar, open: false})}>
        <Alert onClose={() => setSnackbar({...snackbar, open: false})} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Locations; 